import { Inject, Injectable, OnDestroy } from '@angular/core';
import { firstValueFrom, Observable, Subject } from "rxjs";
import {
    SharedWorkerConnectMessage,
    SharedWorkerMessage,
    SharedWorkerMessageType, SharedWorkerSocketInboundMessage,
    SharedWorkerSocketMessage, SocketAlertMessageData, SocketConnectionLocalParams,
    SocketMessageAction
} from "@shared/shared-worker/SharedWorker";
import { LocalStorageService } from "ngx-webstorage";
import { AuthService } from "@shared/services/auth.service";
import { ToasterService } from "@shared/services/toaster.service";
import { TranslateService } from '@tolgee/ngx';


@Injectable({
    providedIn: 'root'
})
export class SharedWorkerService implements OnDestroy {
    private readonly disconnector = this.disconnect.bind(this);

    private id: string;

    private _sharedWorker: SharedWorker;
    private _webWorker: Worker;

    private _message: Subject<any> = new Subject();
    private _socketMessage = new Subject<SharedWorkerSocketInboundMessage>();

    private _lastConnectParams?: SocketConnectionLocalParams;

    public message$: Observable<any> = this._message.asObservable();
    public socketMessage$: Observable<any> = this._socketMessage.asObservable();

    constructor(
        @Inject("environment") private environment: any,
        private lcs: LocalStorageService,
        private _authService: AuthService,
        private toastr: ToasterService,
        private _translateService: TranslateService
    ) {
        this.connect();
    }

    ngOnDestroy() {
        this.disconnect();
        this._message.complete();
        this._socketMessage.complete();
    }

    public connect() {
        if (!this.worker) {
            switch (this.environment?.name) {
                case "local":
                    this.initializeWorker(`../shared-worker`, "ts");
                    break;
                default:
                    this.initializeWorker(`${this.environment?.baseSiteUrl}${this.environment?.baseSiteUrl?.endsWith("/") ? '' : '/'}common/worker`)
            }
        }

        let port;
        if (this.worker && "SharedWorker" in window) {
            (this.worker as SharedWorker).port.start();
            port = (this.worker as SharedWorker).port;
        } else {
            port = this.worker as Worker;
        }
        if (port) {
            port.onmessage = this._handleMessage.bind(this);
        }
        window?.addEventListener("beforeunload", this.disconnector);
    }

    public initializeWorker(scriptDir: string, extension: string = "js") {
        const workerType = ("SharedWorker" in window) ? "shared" : "web";
        const workerUrl = `${scriptDir}/${workerType}.worker.${extension}`;
        if (workerType === "shared") {

            if (this.environment?.name === 'local') {
                this.worker = new SharedWorker(
                    new URL('../shared-worker/shared.worker.ts', import.meta.url),
                    { name: "LISGlobalSharedWorker", type: "module" }
                );
            } else {
                this.worker = new SharedWorker(
                    new URL(workerUrl, import.meta.url),
                    { name: "LISGlobalSharedWorker", type: "module" }
                );
            }

            return;
        }

        this.worker = new Worker(
            new URL(workerUrl, import.meta.url),
            { name: "LISDedicatedWorker", type: "module" }
        );
    }

    public async socketConnect(params?: SocketConnectionLocalParams, retryCount: number = 0): Promise<void> {
        const sessionToken: string = this.lcs.retrieve("session_token");
        const installationId: string = this.lcs.retrieve("installation_id");
        if (!sessionToken) {
            console.error("Could not connect to socket. No session token found.");
            return;
        }

        if (params) {
            this._lastConnectParams = params;
        }

        try {
            let url: string;

            if (this.environment?.name === "local") {
                // TODO: Replace with ticket-based auth once the local backend supports it.
                url = `${this.environment?.socketUrl}?app=lis&sessionToken=${sessionToken}&installationId=${installationId}`;
                for (const [key, value] of Object.entries(this._lastConnectParams ?? {})) {
                    url += `&${key}=${value}`;
                }
            } else {
                const ticket = await firstValueFrom(this._authService.getWsTicket(sessionToken, installationId));
                url = `${this.environment?.socketUrl}?app=lis&ticket=${ticket}&installationId=${installationId}`;
            }

            this.postMessage<SharedWorkerConnectMessage>({ url, retryCount }, "connect");
        } catch (error: unknown) {
            console.error("Failed to initiate socket connection", error);
        }
    }

    public disconnect() {
        this.postMessage(null, "disconnect");
        if ("SharedWorker" in window) {
            (this.worker as SharedWorker)?.port.close();
        } else {
            (this.worker as Worker)?.terminate();
        }
        this.worker = null;
        window?.removeEventListener("beforeunload", this.disconnector);
    }

    public postMessage<T = any>(message: T, type: SharedWorkerMessageType = "generic") {
        this._postMessage({
            type,
            platform: this.environment?.slug || "unknown",
            payload: message
        });
    }

    public socketMessage(event: string, message: Omit<SharedWorkerSocketMessage, "event">) {
        this.postMessage({ ...message, event }, "socketMessage");
    }

    private _postMessage(message: SharedWorkerMessage) {

        let port;
        if (this.worker && "SharedWorker" in window) {
            port = (this.worker as SharedWorker).port;
        } else {
            port = this.worker as Worker;
        }

        port?.postMessage(message);
    }

    private _handleMessage(message: MessageEvent<SharedWorkerMessage>) {

        switch (message.data.type) {
            case "socketMessage":
                this._handleSocketMessage(message.data.payload);
                break;
            case "socketReconnect":
                this.socketConnect(undefined, message.data.payload?.retryCount ?? 0);
                break;
            // @ts-ignore
            case "connection":
                if (!message.data.payload?.socket) {
                    this.id = message.data.payload.id;
                }
            default:
                this._message.next(message.data);
        }
    }

    private _handleSocketMessage(message: SharedWorkerSocketInboundMessage) {
        if (!message) {
            return;
        }
        message.action = message.action?.toUpperCase() as SocketMessageAction;
        switch (message.action) {
            case SocketMessageAction.ALERT:
                const alert = (message.data as SocketAlertMessageData).alert;
                (this.toastr[alert.level] ?? this.toastr.info)
                    .bind(this.toastr)
                    (
                        this._translateService.instant("notification"),
                        this._translateService.instant(alert.message),
                    );
                break;
            default:
                this._socketMessage.next(message);
        }


        if (this.environment?.name === "local") {
            console.log(
                `[SharedWorker socket]: ${message.action} ${message.critical ? "!!CRITICAL!!" : ""}`,
                message.data,
                `Scope: ${message.namespace || "GLOBAL"} (${message.rooms?.join(", ") || ""})`,
            );
        }
    }

    get tabId(): string {
        return this.id;
    }

    private get worker(): SharedWorker | Worker {
        return this._sharedWorker ?? this._webWorker;
    }

    private set worker(value: SharedWorker | Worker) {
        if ("SharedWorker" in window) {
            this._sharedWorker = value as SharedWorker;
            this._webWorker = null;
            return;
        }
        this._webWorker = value as Worker;
        this._sharedWorker = null;
    }
}
